"use client";
import { useState, useEffect, useRef, ChangeEvent } from "react";
import Modal from "react-bootstrap/Modal";
import Datagrid from "../../components/Datagrid/Datagrid";
import { IoLocationOutline } from "react-icons/io5";
import ModalIconTitle from "../../components//ModalIconTitle";
import common from "../../utils/common";
import type toast from "react-hot-toast";
import CalendarBackend from "../../components/Calendar/utils/CalendarBackend";
import { AgGridReact } from "ag-grid-react";
import { ColDef, SelectionChangedEvent } from "ag-grid-community";
import CreateEditDeleteButtonGroup from "../../components/CreateEditDeleteButtonGroup";
import SearchInput from "../../components/Datagrid/SearchInput";
import { cal_place, cal_calendar } from "../../class/prisma/db";
import PlaceModal from "./PlaceModal";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { ColorColumnCell } from "../../components/ColorColumnCell";
import { SystemMsg } from "../../utils/SystemMsg";
import { setPlaces } from "../../store/features/cacheSlice";
import { useI18n } from "../../i18n";


type PlaceListModalProps = {
  show: boolean;
  onClose: () => void;
  toast: typeof toast;
};

function PlaceListModal({ show, onClose, toast }: PlaceListModalProps) {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const title = t("place.listTitle");
  const [rowData, setRowData] = useState<cal_place[]>([]);
  const [gridLoading, setGridLoading] = useState<boolean>(true);
  const gridRef = useRef<AgGridReact>(null);
  const calendars = useSelector((state: RootState) => state.cache.calendars);

  const [initialized, setInitialized] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [paginationPage, setPaginationPage] = useState(0);

  const [selectedId, setSelectedId] = useState(-1);
  const [crudMode, setCrudMode] = useState<"add" | "edit">("add");
  const [showModal, setShowModal] = useState(false);
  const [modalDataObj, setModalDataObj] = useState<cal_place | null>(null);

  const [keyword, setKeyword] = useState("");

  const [modalClassName, setModalClassName] = useState("modal-55w");

  const calendarNameRenderer = (params: any) => {
    const calendarName = params.value;
    const calendarColor = params.data.calendarColor;
    return <ColorColumnCell color={calendarColor} text={calendarName} />;
  };

  const colDefs = [
    { field: "id", width: 50 },
    {
      field: "calendarName",
      headerName: "Calendar",
      width: 160,
      cellRenderer: calendarNameRenderer,
    },
    { field: "name", flex: 1 },
    { field: "address", flex: 2 },
  ] as ColDef[];

  const getRowData = (updateGrid = true) => {
    setGridLoading(true);
    CalendarBackend.getPlaceData(
      (data) => {
        let placeData = data.data;
        if (!placeData || !Array.isArray(placeData)) {
          return;
        }
        if (placeData) {
          const total =
            data.total != null && !Number.isNaN(data.total.toString())
              ? data.total
              : 0;
          setPageCount(common.getPageCount(total, pageSize));
          setRowData(transformRowData(placeData));
          setGridLoading(false);
        }
        dispatch(setPlaces(placeData));
      },
      keyword,
      page,
      pageSize,
    );
  };

  const getCalendarObject = (id: number): cal_calendar | undefined => {
    if (!Array.isArray(calendars) || (calendars as any[]).length === 0) {
      return;
    }
    const list = calendars as cal_calendar[];
    const find = list.find((x) => x.id === id);
    return find;
  };

  const transformRowData = (data: any[]) => {
    const dataArray: any[] = [];
    if (!Array.isArray(data)) {
      return dataArray;
    }

    for (const item of data) {
      const calendar = getCalendarObject(item.calendar_id);
      const calendarName = calendar?.name ?? "";
      const calendarColor = calendar?.color ?? "";
      dataArray.push({
        ...item,
        calendarName: calendarName,
        calendarColor: calendarColor,
      });
    }

    return dataArray;
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    if (!initialized) {
      getRowData();
      setInitialized(true);
      return;
    }

    const debounce = setTimeout(() => {
      if (initialized) {
        getRowData();
      }
    }, common.DEBOUNCE_TIME);
    return () => clearTimeout(debounce);
  }, [show, keyword, page, pageSize]);

  useEffect(() => {
    setPage(1);
    setPaginationPage(0);
  }, [pageSize]);

  useEffect(() => {
    blurCurrentModal(showModal);
  }, [showModal]);

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }

    gridRef.current?.api?.deselectAll();
    setRowData([]);
    setGridLoading(true);
    setPaginationPage(0);
    setPage(1);
    setSelectedId(-1);
    setInitialized(false);
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    if (
      !event ||
      !event.selectedNodes ||
      event.selectedNodes === null ||
      !Array.isArray(event.selectedNodes) ||
      event.selectedNodes.length === 0
    ) {
      setSelectedId(-1);
      return;
    }
    const selectedNode = event.selectedNodes[0];
    setSelectedId(selectedNode.data.id);
  };

  const handlePageClick = (event: { selected: number }) => {
    if (!event || event?.selected == null || Number.isNaN(event.selected)) {
      return;
    }
    setPage(event.selected + 1);
    setPaginationPage(event.selected);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (
      !event ||
      !event?.target ||
      event?.target?.value == null ||
      Number.isNaN(event?.target?.value)
    ) {
      return;
    }
    setPageSize(Number(event.target.value));
  };

  const onCreateButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCrudMode("add");
    setShowModal(true);
    setModalDataObj(null);
  };

  const onEditButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCrudMode("edit");
    setShowModal(true);
    const dataObj = rowData.find((x) => x.id === selectedId) || null;
    setModalDataObj(dataObj);
  };

  const onDeleteButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const find = rowData?.find((x) => x.id === selectedId);
    if (!find) {
      toast.error(SystemMsg.GridUi.emptySelection);
      return;
    }
    CalendarBackend.deletePlace(
      selectedId,
      (res) => {
        if (res.status !== 200) {
          toast.error(SystemMsg.Calendar.deleteFailed(find.name));
          return;
        }
        toast.success(SystemMsg.Calendar.deleteSuccess(find.name));
        gridRef?.current?.api.applyTransaction({ remove: [find] });
        const findIdx = rowData?.indexOf(find);
        if (!findIdx) {
          return;
        }
        rowData?.splice(findIdx, 1);
        getRowData(false);
      },
      (err) => {
        toast.error(SystemMsg.Calendar.deleteFailed(find.name));
        return;
      },
    );
  };

  const blurCurrentModal = (blur: boolean) => {
    if (blur) {
      setModalClassName(
        modalClassName.replaceAll(`modal-bg-blurred`, ``) + " modal-bg-blurred",
      );
    } else {
      setModalClassName(modalClassName.replaceAll(` modal-bg-blurred`, ``));
    }
  };

  const topLeftElement = (
    <SearchInput searchValue={keyword} setSearchValueFunc={setKeyword} />
  );

  const topMenuElement = (
    <CreateEditDeleteButtonGroup
      onCreateButtonClick={onCreateButtonClick}
      onEditButtonClick={onEditButtonClick}
      onDeleteButtonClick={onDeleteButtonClick}
      hasEdit={true}
      disableEdit={selectedId === -1}
      hasDelete={selectedId !== -1}
    />
  );

  const savePlaceClick = (boolVal: boolean, data: cal_place) => {
    setShowModal(false);
    setModalDataObj(null);
    gridRef.current?.api?.deselectAll();
    if (!boolVal) {
      return;
    }
    if (crudMode === "add") {
      addPlaceCallback(data);
    } else if (crudMode === "edit") {
      editPlaceCallback(data);
    }
  };

  const addPlaceCallback = (data: cal_place) => {
    getRowData();
  };

  const editPlaceCallback = (data: cal_place) => {
    if (!data) {
      toast.error(SystemMsg.GridUi.emptyData);
      return;
    }
    const obj = data as cal_place;
    if (obj.id && obj.id.toString().length > 0) {
      obj.id = Number(obj.id);
    }
    const find = rowData?.find((x) => x.id.toString() === obj.id.toString());
    if (!find) {
      toast.error(SystemMsg.GridUi.failedToFindRowById(obj.id));
      return;
    }

    const findIdx = rowData?.indexOf(find);
    if (!find || findIdx === undefined || findIdx < 0) {
      toast.error(SystemMsg.GridUi.doesNotIncludeRecord(obj.id));
      return;
    }
    const tempArray = [obj];
    rowData[findIdx] = tempArray[0];
    const rowNode = gridRef?.current?.api?.getRowNode(findIdx.toString());
    rowNode?.setData(tempArray[0]);
    getRowData(false);
  };

  return (
    <>
      <Modal
        size="lg"
        show={show}
        onHide={handleClose}
        backdrop="static"
        dialogClassName={modalClassName}
        centered
      >
        <Modal.Header
          style={{ border: "none", paddingBottom: "4px" }}
          closeButton
        >
          <Modal.Title>
            <ModalIconTitle title={title} iconElement={<IoLocationOutline />} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Datagrid
            rowSelect="singleRow"
            height="60vh"
            gridRef={gridRef}
            rowData={rowData}
            colDefs={colDefs}
            onSelectionChanged={onSelectionChanged}
            hasPagination
            handlePageClick={handlePageClick}
            handlePageSizeChange={handlePageSizeChange}
            paginatePage={paginationPage}
            paginatePageCount={pageCount}
            topLeftElement={topLeftElement}
            topRightElement={topMenuElement}
            loading={gridLoading}
          />
        </Modal.Body>
      </Modal>
      <PlaceModal
        toast={toast}
        mode={crudMode}
        show={showModal}
        onSaveBtnClick={savePlaceClick}
        onClose={() => setShowModal(false)}
        data={modalDataObj}
      />
    </>
  );
}

export default PlaceListModal;
